import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSideDialog } from '../../hooks';
import { SkeletonLoader, Button } from '../';

import './SideDialog.css';

const withContent = WrappedComponent => {
  // Use a HOC to add loading state alongside the form content.
  // The component reads sideDialog from context directly so that updates to
  // sideDialog (e.g. formErrors) re-render in place without unmounting the
  // form — which would lose all user-entered data.

  return () => {
    const [loading, setLoading] = useState(false);
    const { sideDialog, loadingSideDialog } = useSideDialog();
    const { submitLabel, submitLabelLoading, cancelLabel, onSubmit, onCancel, formRef } =
      sideDialog || {};

    return (
      <React.Fragment>
        {(WrappedComponent && (
          <div className="side-dialog-content">
            <WrappedComponent
              {...sideDialog}
              loading={loading}
              setLoading={setLoading}
              ref={formRef}
            />
          </div>
        )) || (
          <React.Fragment>
            <SkeletonLoader />
            <ul>
              <SkeletonLoader width={150} />
            </ul>
          </React.Fragment>
        )}

        <div className="side-dialog-footer">
          <Button
            onClick={onSubmit}
            buttonStyle="main-button"
            value={loading ? submitLabelLoading : submitLabel}
            className={loading ? ' loading' : ''}
            disabled={loadingSideDialog || loading}
          />
          <Button onClick={onCancel} value={cancelLabel} disabled={loadingSideDialog || loading} />
        </div>
      </React.Fragment>
    );
  };
};

export const SideDialog = () => {
  const initialWidth = 450;

  const [sideDialogWidth, setSideDialogWidth] = useState(
    parseInt(localStorage.getItem('sideDialogWidth')) || initialWidth
  ); // initial width of the div
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const rawWidthRef = useRef(parseInt(localStorage.getItem('sideDialogWidth')) || initialWidth);

  const { show, sideDialog, loadingSideDialog } = useSideDialog();

  const { title, Content, onCancel } = sideDialog || {};

  const Component = useMemo(() => withContent(Content), [Content]);

  const portalRef = document.getElementById('portal');

  const startResizing = useCallback(e => {
    isResizingRef.current = true;
    startXRef.current = e.clientX;
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    if (isResizingRef.current) {
      isResizingRef.current = false;
      setIsResizing(false);
      // Persist on drag end only — avoids synchronous I/O on every pixel change
      // Sync rawWidthRef so the next drag session starts from the displayed width
      setSideDialogWidth(prev => {
        rawWidthRef.current = prev;
        localStorage.setItem('sideDialogWidth', prev);
        return prev;
      });
    }
  }, []);

  const handleMouseMove = useCallback(e => {
    if (!isResizingRef.current) return;
    const deltaX = e.clientX - startXRef.current;
    // Accumulate movement in ref so small increments can escape the snap zone
    rawWidthRef.current = Math.max(rawWidthRef.current - deltaX, 200);
    const displayWidth =
      Math.abs(rawWidthRef.current - initialWidth) <= 10 ? initialWidth : rawWidthRef.current;
    setSideDialogWidth(displayWidth);
    startXRef.current = e.clientX;
  }, []); // no deps — reads from refs, never recreated during drag

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, []); // registered once on mount, never torn down mid-drag

  return (
    <div
      id="SideDialog"
      className={'SideDialog' + (show ? ' show' : '') + (isResizing ? ' is-resizing' : '')}
      style={{
        width: `${sideDialogWidth}px`,
        right: `-${sideDialogWidth}px`,
      }}
    >
      <div className={'side-dialog-container'}>
        <div className="side-dialog-header">
          <h2>{title}</h2>
          <div className="close-button" onClick={onCancel}></div>
        </div>

        <Component />

        <div className="resize-control" onMouseDown={startResizing} />
      </div>
    </div>
  );
};
